import { ButtonWithConfirmation } from '@readcloud/component-library';
import {
  Annotation,
  KnownCloud,
  KnownUser,
  UserPrefState,
} from '@readcloud/data';
import { Popover, PopoverContent, PopoverTrigger, Small } from '@readcloud/ui';
import tailwindConfig from '@root/tailwind.config';
import React, { RefObject } from 'react';
import { Button } from '@readcloud/component-library';
import { ButtonGroup } from '@readcloud/ui/button-group';
import { MdDelete, MdEdit, MdShare } from 'react-icons/md';
import { AnnotationEditor } from './AnnotationEditor';
import { SharingPopover } from './SharingPopover';

interface Props {
  containerRef: RefObject<HTMLDivElement>;
  outerContainer: HTMLDivElement;
  pagesContainer: HTMLDivElement;
  pageContainer: HTMLDivElement;
  annotation: Annotation;
  reference?: HTMLElement;
  onDelete: (id: string) => void;
  onComment: (id: string) => void;
  onChange: (annotation: Annotation) => void;
  knownUsers: KnownUser[];
  knownClouds: KnownCloud[];
  showPublisherOptions: boolean;
  zIndex: number;
  onSavePreferences?: (type: string, data: Partial<Annotation['data']>) => void;
  annoPrefs?: UserPrefState['annoPrefs'];
  onEditOpen?: (open: boolean | ((value: boolean) => boolean)) => void;
  onShareOpen?: (open: boolean | ((value: boolean) => boolean)) => void;
  editOpen?: boolean;
  shareOpen?: boolean;
}

export const AnnotationToolbarPopovers = (props: Props) => {
  const handleDelete = React.useCallback(() => {
    if (props.annotation) props.onDelete(props.annotation.id);
  }, [props.annotation && props.annotation.id, props.onDelete]);

  const mobileSizeinPx = tailwindConfig?.theme?.screens?.['sm'] || '640px';
  const mobileSize = parseInt(mobileSizeinPx, 10);

  const mobileView = React.useMemo(() => {
    if (mobileSize) {
      return window.innerWidth <= mobileSize;
    }
    return false;
  }, [mobileSizeinPx, window.innerWidth]);

  return (
    !mobileView && (
      <>
        <ButtonGroup className="rc-shadow-md rc-overflow-hidden rc-rounded-md rc-z-[1500] rc-isolate">
          <Popover
            open={props.editOpen}
            onOpenChange={() => props.onEditOpen((s) => !s)}
          >
            <PopoverTrigger asChild>
              <Button variant="light" className="rc-h-16">
                <div className="rc-flex rc-flex-col rc-items-center rc-gap-2">
                  <MdEdit size="24" />
                  <Small>Edit</Small>
                </div>
              </Button>
            </PopoverTrigger>
            <PopoverContent
              portalled={false}
              className="rc-border-0 rc-shadow-md rc-rounded-md rc-z-[1600] rc-hidden sm:rc-block rc-max-h-[var(--radix-popover-content-available-height)] rc-overflow-auto"
              collisionPadding={32}
              collisionBoundary={props.pageContainer}
            >
              <AnnotationEditor
                showPublisherOptions={props.showPublisherOptions}
                annotation={props.annotation}
                submitLabel="Save"
                onChange={props.onChange}
                autoSubmit
                onSavePreferences={props.onSavePreferences}
                annoPrefs={props.annoPrefs}
              />
            </PopoverContent>
          </Popover>
          <ButtonWithConfirmation
            title="Delete Annotation"
            confirmText="Delete"
            confirmVariant="danger"
            message="Are you sure you want to delete this annotation?"
            onConfirm={handleDelete}
            renderButton={
              <Button variant="light" className="rc-h-16">
                <div className="rc-flex rc-flex-col rc-items-center rc-gap-2">
                  <MdDelete size="24" />
                  <Small>Delete</Small>
                </div>
              </Button>
            }
          />
          {props.knownUsers.length || props.knownClouds.length ? (
            <Popover
              open={props.shareOpen}
              onOpenChange={() => props.onShareOpen((s) => !s)}
            >
              <PopoverTrigger asChild>
                <Button variant="light" className="rc-h-16">
                  <div className="rc-flex rc-flex-col rc-items-center rc-gap-2">
                    <MdShare size="24" />
                    <Small>Share</Small>
                  </div>
                </Button>
              </PopoverTrigger>
              <PopoverContent
                portalled={false}
                className="rc-border-0 rc-max-w-fit rc-w-[25rem] rc-shadow-md rc-rounded-md rc-z-[1600] rc-hidden sm:rc-block rc-max-h-[var(--radix-popover-content-available-height)] rc-overflow-auto"
                collisionPadding={32}
                collisionBoundary={props.pageContainer}
              >
                <SharingPopover
                  knownUsers={props.knownUsers}
                  knownClouds={props.knownClouds}
                  annotation={props.annotation}
                  onChange={props.onChange}
                />
              </PopoverContent>
            </Popover>
          ) : null}
        </ButtonGroup>
      </>
    ) 
  );
};

export default AnnotationToolbarPopovers;
